import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { HeadlineMedium, LabelMedium, BodyMedium } from './Typography';

const PasswordReset = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [validCode, setValidCode] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get the action code from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const actionCode = urlParams.get('oobCode');
  const mode = urlParams.get('mode');
  
  // Handle Firebase auth action URLs that might come from the default domain
  useEffect(() => {
    // If we're on the Firebase domain, redirect to our custom domain
    if (window.location.hostname.includes('firebaseapp.com')) {
      const newUrl = `https://learn.storykin.app/reset-password${window.location.search}`;
      window.location.replace(newUrl);
      return;
    }
  }, []);

  useEffect(() => {
    if (mode === 'resetPassword' && actionCode) {
      // Verify the password reset code and get the email
      verifyPasswordResetCode(auth, actionCode)
        .then((email) => {
          setEmail(email);
          setValidCode(true);
        })
        .catch((error) => {
          console.error('Invalid or expired reset code:', error);
          setError('This password reset link is invalid or has expired. Please request a new one.');
        });
    } else {
      setError('Invalid password reset link.');
    }
  }, [actionCode, mode]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newPassword) {
      setError('Please enter a new password.');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setLoading(true);
    
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setSuccess(true);
    } catch (error) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/sign-in');
  };

  if (success) {
    return (
      <div className="min-h-screen flex bg-gray-50">
        {/* Left Column - Marketing Content */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#282828' }}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10" aria-hidden="true">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <pattern id="marketing-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="10" cy="10" r="1" fill="white" opacity="0.3"/>
              </pattern>
              <rect width="100" height="100" fill="url(#marketing-pattern)"/>
            </svg>
          </div>
          
          {/* Marketing Content */}
          <div className="relative z-10 flex flex-col justify-center pl-24 pr-12 py-16 text-white">
            {/* Logo */}
            <div className="mb-4">
              <img 
                src="/images/storykin_logo.png" 
                alt="STORYKIN" 
                className="w-64 h-auto object-contain"
              />
            </div>
            
            {/* Success Message */}
            <h1 className="text-5xl font-bold leading-tight mb-8">
              Password Reset
              <br />
              <span className="text-3xl">Successful!</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
          </div>
        </div>

        {/* Right Column - Success Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <HeadlineMedium color="gray-900">Password Reset Complete</HeadlineMedium>
              <BodyMedium color="gray-600" className="mt-2">
                Your password has been successfully updated for {email}
              </BodyMedium>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg aria-hidden="true" className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <BodyMedium color="green-800">
                  Password successfully updated!
                </BodyMedium>
              </div>
            </div>

            <button
                onClick={handleBackToLogin}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                style={{ backgroundColor: '#282828' }}
              >
                <LabelMedium color="white">Continue to Sign In</LabelMedium>
              </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Column - Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#282828' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="marketing-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="1" fill="white" opacity="0.3"/>
            </pattern>
            <rect width="100" height="100" fill="url(#marketing-pattern)"/>
          </svg>
        </div>
        
        {/* Marketing Content */}
        <div className="relative z-10 flex flex-col justify-center pl-24 pr-12 py-16 text-white">
          {/* Logo */}
          <div className="mb-4">
            <img 
              src="/images/storykin_logo.png" 
              alt="STORYKIN" 
              className="w-64 h-auto object-contain"
            />
          </div>
          
          {/* Headline */}
          <h1 className="text-5xl font-bold leading-tight mb-8">
            Reset Your Password
            <br />
            <span className="text-3xl">Almost there!</span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8">
            Enter your new password below to complete the reset process.
          </p>
        </div>
      </div>

      {/* Right Column - Reset Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <HeadlineMedium color="gray-900">Reset your password</HeadlineMedium>
            {validCode && (
              <BodyMedium color="gray-600" className="mt-2">
                for {email}
              </BodyMedium>
            )}
          </div>

          {/* role=alert announces errors to AT immediately; id links inputs via aria-describedby */}
          {error && (
            <div
              id="password-reset-error"
              role="alert"
              className="bg-red-50 border border-red-200 rounded-lg p-4"
            >
              <BodyMedium color="red-800">{error}</BodyMedium>
            </div>
          )}

          {validCode ? (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label htmlFor="new-password" className="sr-only">
                  New password
                </label>
                <input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-describedby={error ? 'password-reset-error' : undefined}
                  aria-invalid={!!error}
                  className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="sr-only">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-describedby={error ? 'password-reset-error' : undefined}
                  aria-invalid={!!error}
                  className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                style={{ backgroundColor: '#282828' }}
              >
                <LabelMedium color="white">
                  {loading ? 'Updating Password...' : 'SAVE'}
                </LabelMedium>
              </button>
            </form>
          ) : (
            <div className="text-center">
              <button
                onClick={handleBackToLogin}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                style={{ backgroundColor: '#282828' }}
              >
                <LabelMedium color="white">Back to Sign In</LabelMedium>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
